import React, { useMemo } from 'react';
import { Card, Bubble } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { NOTEWORTHYGAMES, BIGRISER } from '../_config/constant';
import { DataSourceType } from '../_config/interface';
import { useIntl, history } from 'umi';
import Abnormal from '@/pages/home/components/Abnormal';
import { changeURLStatic, newReleaseUpcommingPath } from '@/pages/home/utils/urlConfig';
import { DataSourceIcon } from '@/pages/home/pages/home/components/DataSource';
import { getSpotlightConfig } from '../_config/spotlightColumns';
import './GameSpotlight.scss';
import NoData from '@intelligence/components/NoData';

export default function GameSpotlight(props: any) {
  const { loading, error, data, cardName, entityType, midUrlList } = props;
  const { formatMessage } = useIntl();

  const list = data?.list || [];

  /** 从配置文件获取卡片配置（列 + 空态提示 + 标题） */
  const cardConfig = useMemo(() => {
    return getSpotlightConfig(entityType, cardName, formatMessage);
  }, [entityType, cardName, formatMessage]);

  const columns = cardConfig.columns;
  const nodataMsg = cardConfig.nodataMsg;
  const cardTitle = cardConfig.cardTitle || cardName;

  /** 获取标题跳转链接 */
  const cardTitleUrl = useMemo(() => {
    if (cardName === BIGRISER || cardName === NOTEWORTHYGAMES) {
      return '';
    }

    let titleUrl = '';
    try {
      midUrlList?.forEach((e: DataSourceType) => {
        const [entityName = 'Mobile', cardNameStr = ''] = e.link_name.split('_');
        if (cardName?.indexOf(cardNameStr) > -1 && entityName?.toLocaleLowerCase() === entityType) {
          titleUrl = newReleaseUpcommingPath[e?.link_name];
        }
      });
    } catch (err) {
      console.log(err);
    }

    return titleUrl;
  }, [cardName, midUrlList, entityType]);

  /** 渲染列头 icon（支持单个或多个，icon 间距 2px，与文字间距 4px） */
  const renderColumnIcon = (icon?: string | string[]) => {
    if (!icon) return null;
    const icons = Array.isArray(icon) ? icon : [icon];
    return (
      <span className="dbh-game-spotlight-header__icons">
        {icons.map((ic) => (
          <DataSourceIcon key={ic} className="dbh-game-spotlight-header__icon" type={ic} />
        ))}
      </span>
    );
  };

  /** 渲染表头行 */
  const renderHeader = () => (
    <div className="dbh-game-spotlight-header">
      {columns.map((col) => {
        const { tooltipContent, learnMoreUrl } = col as any;
        const hasTooltip = !!tooltipContent;

        const titleNode = (
          <div
            className={`dbh-game-spotlight-header__cell dbh-game-spotlight-header__cell--${col.align || 'left'}${hasTooltip ? ' dbh-game-spotlight-header__cell--hoverable' : ''}`}
            style={col.key === 'game' ? { flex: 1 } : { width: col.width, flexShrink: 0 }}
          >
            {renderColumnIcon(col.icon)}
            <span className="dbh-game-spotlight-header__text">{col.title}</span>
          </div>
        );

        if (!hasTooltip) {
          return <React.Fragment key={col.key}>{titleNode}</React.Fragment>;
        }

        const bubbleContent = (
          <span className="dbh-game-spotlight-tooltip">
            {tooltipContent}
            {learnMoreUrl && (
              <span
                style={{ color: '#326FF0', paddingLeft: '5px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  history.push(learnMoreUrl);
                }}
              >
                {formatMessage({ id: 'dbh_learnmore' })}
              </span>
            )}
          </span>
        );

        return (
          <Bubble key={col.key} dark style={{ maxWidth: 450 }} content={bubbleContent}>
            {titleNode}
          </Bubble>
        );
      })}
    </div>
  );

  /** 渲染单行数据 */
  const renderRow = (item: any, index: number) => (
    <div className="dbh-game-spotlight-row" key={item?.app_id || item?.id || index}>
      {columns.map((col) => (
        <div
          key={col.key}
          className={`dbh-game-spotlight-row__cell dbh-game-spotlight-row__cell--${col.align || 'left'}`}
          style={col.key === 'game' ? { flex: 1, minWidth: 0 } : { width: col?.tbodyColWidth ?? col.width, flexShrink: 0 }}
        >
          {col.render(item, entityType, cardName)}
        </div>
      ))}
    </div>
  );

  return (
    <Card className="dbh-game-spotlight">
      <Card.Body
        title={
          !cardTitleUrl ? (
            <div className="dbh-game-spotlight__title">{cardTitle}</div>
          ) : (
            <div
              className="dbh-game-spotlight__title dbh-game-spotlight__title--link"
              onClick={() => changeURLStatic(cardTitleUrl)}
            >
              {cardTitle}
              <SvgIcon className="dbh-game-spotlight__title-icon" type="arrow-right" />
            </div>
          )
        }
        operation={
          <div className="dbh-game-spotlight__time">
            {cardName === BIGRISER && data?.min_date && data?.max_date
              ? `${data.min_date} ~ ${data.max_date}`
              : (data?.max_date || data?.last_date || '')}
          </div>
        }
      >
        <Abnormal
          isLoading={loading}
          isError={error}
          isNoData={false}
          style={{ height: 395, color: '#444' }}
          component={
            <div className="dbh-game-spotlight__table">
              {renderHeader()}
              <div className="dbh-game-spotlight__list">
                {list?.length
                  ? list.map((item: any, index: number) => renderRow(item, index))
                  : (
                    <div className="dbh-game-spotlight__empty">
                      <NoData text={nodataMsg} />
                    </div>
                  )}
              </div>
            </div>
          }
        />
      </Card.Body>
    </Card>
  );
}
